import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import SubHeader from '../../../components/SubHeader.jsx';
import useConvertToBase64 from '../../../hooks/useConvertToBase64.js';
import { updateChangeInsightSettings } from '../../../modules/changeInsights.js';
import { getOrgSlug, mapFinalFormErrors } from '../../../lib/utils.js';
import {
  retrieveOrganization,
  selectOrganization,
} from '../../../modules/organizations.js';
import {
  retrieveUser,
  selectCurrentOrganizationId,
  selectUser as selectCurrentUser,
} from '../../../modules/auth.js';
import { selectUser } from '../../../modules/users.js';
import { CUSTOM_DEFAULT_BRAND_COLOR } from '../../change-insights/utils/index.jsx';
import OrgSettingsHeader from '../../../components/headers/PageHeaders/Organization/OrganizationHeader.jsx';
import OrgCompanySettingsForm from '../../../components/organizations/Settings/CompanySettings/OrgCompanySettingsForm.jsx';
import { customCompanySettingsValidate } from '../utils/customCompany.js';

const mapErrors = mapFinalFormErrors('Failed to save');

const mapStateToProps = (state, ownProps) => ({
  userId: ownProps.userId,
  organization: selectOrganization(state),
  organizationId: selectCurrentOrganizationId(state),
  currentUser: selectCurrentUser(state),
  user: selectUser(state),
});

const mapDispatchToProps = {
  updateChangeInsightSettings,
  retrieveOrganization,
  retrieveUser,
};

const DEFAULT_BRAND_SETTING = {
  brandColor: CUSTOM_DEFAULT_BRAND_COLOR,
  logos: null,
  fontName: {
    id: 'sq_352i_listHelvetica',
    name: 'Nunito, Helvetica, Arial, sans-serif',
  },
  customParentPortfolioName: '',
  customPortfolioName: '',
  customProjectName: '',
  customInitiativeName: '',
  customActivityName: '',
};

const OrgCustomCompanySettingsFormContainer = (props) => {
  const {
    organizationId,
    organization,
    retrieveOrganization,
    retrieveUser,
    updateChangeInsightSettings,
  } = props;

  const slug = getOrgSlug();

  const [selectedImage, setSelectedImage] = useState('');
  const [selectedIconLogo, setSelectedIconLogo] = useState('');
  const [imageSize, setImageSize] = useState('');
  const [iconLogoSize, setIconLogoSize] = useState('');

  const [data, setData] = useState(DEFAULT_BRAND_SETTING);
  const { convertToBase64 } = useConvertToBase64();

  const onDeleteLogo = () => {
    setSelectedImage(null);
  };
  const onDeleteIcon = () => {
    setSelectedIconLogo(null);
  };

  const redirectToSettings = () => {
    window.location.href = `/${slug}/organization/custom-settings`;
  };

  const onSubmit = (values) => {
    const resource = {};
    if (data.brandColor) {
      resource.brandColor = data.brandColor;
    }
    if (selectedIconLogo || selectedIconLogo === null) {
      resource.icon = selectedIconLogo;
    }
    if (data.fontName.name) {
      resource.fontName = data.fontName.name;
    }
    if (selectedImage) {
      resource.logos = [selectedImage];
    }

    if (selectedImage === null) {
      resource.logos = [];
    }

    resource.useOktaSso = values.useOktaSso;

    if (values.useOktaSso) {
      resource.ssoSettings = {
        clientId: values.clientId,
        issuer: values.issuerUri,
      };
    }

    if (values) {
      resource.customNaming = {
        parentPortfolio: {
          default: 'parent portfolio',
          custom: values.customParentPortfolioName,
        },
        portfolio: { default: 'portfolio', custom: values.customPortfolioName },
        project: { default: 'project', custom: values.customProjectName },
        initiative: {
          default: 'initiative',
          custom: values.customInitiativeName,
        },
        activity: { default: 'activity', custom: values.customActivityName },
      };
    }

    const submitPromise = updateChangeInsightSettings(resource);

    return submitPromise
      .then(() => {
        retrieveUser({ organization_slug: slug }).then((response) => {
          if (response) {
            response.user.organizations.map((o) =>
              localStorage.setItem(
                `${o.slug}_CUSTOM_NAMING_SETTING`,
                JSON.stringify(o.customNaming)
              )
            );
          }
        });
        retrieveOrganization(organizationId).then(() => redirectToSettings());
      })
      .catch(mapErrors);
  };

  const validate = (values) =>
    customCompanySettingsValidate(
      values,
      imageSize,
      selectedImage,
      iconLogoSize,
      selectedIconLogo
    );

  const handleChangeData = (value, key) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    const base64 = await convertToBase64(file);

    setSelectedImage(base64);
    setImageSize(file.size);
  };

  const handleIconLogoSelect = async (e) => {
    const file = e.target.files[0];
    const base64 = await convertToBase64(file);

    setSelectedIconLogo(base64);
    setIconLogoSize(file.size);
  };

  useEffect(() => {
    if (organization) {
      const updatedData = { ...data };

      if (organization.useOktaSso) {
        updatedData.useOktaSso = organization.useOktaSso;
        updatedData.clientId = organization.ssoSettings.clientId;
        updatedData.issuerUri = organization.ssoSettings.issuer;
      }

      if (organization.brandColor) {
        updatedData.brandColor = organization.brandColor;
      }

      if (organization.logos.length) {
        updatedData.logos = organization.logos[organization.logos.length - 1];
      }

      if (organization.fontName) {
        updatedData.fontName = { name: organization.fontName };
      }

      if (organization.icon) {
        updatedData.icon = organization.icon;
      }

      if (organization.customNaming) {
        const { customNaming } = organization;

        if (customNaming?.parentPortfolio.custom) {
          updatedData.customParentPortfolioName =
            customNaming.parentPortfolio.custom;
        }

        if (customNaming?.portfolio.custom) {
          updatedData.customPortfolioName = customNaming.portfolio.custom;
        }

        if (customNaming?.project.custom) {
          updatedData.customProjectName = customNaming.project.custom;
        }

        if (customNaming?.initiative.custom) {
          updatedData.customInitiativeName = customNaming.initiative.custom;
        }
        if (customNaming?.activity.custom) {
          updatedData.customActivityName = customNaming.activity.custom;
        }
      }

      setData(updatedData);
    } else {
      retrieveOrganization(organizationId);
    }
  }, [organization]);

  return (
    <React.Fragment>
      <OrgSettingsHeader slug={slug} page="Edit Organization Settings" />
      <SubHeader flexJustify small>
        <div className="flex row">
          <h2 className="m-r-10 m-l-16">Edit Organization Settings</h2>
        </div>
      </SubHeader>

      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  <Form
                    component={OrgCompanySettingsForm}
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={data}
                    handleCancel={redirectToSettings}
                    handleChangeData={handleChangeData}
                    handleImageSelect={handleImageSelect}
                    handleIconLogoSelect={handleIconLogoSelect}
                    onDeleteLogo={onDeleteLogo}
                    onDeleteIcon={onDeleteIcon}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrgCustomCompanySettingsFormContainer);
